import moment from 'moment';
import {
    filter,
    upperFirst,
    camelCase,
} from 'lodash';
import store from '@/store';
import axios from 'axios';

/**
 * Checks if user is authenticated
 *
 * @returns {Function} Callback for navigation guard
 */
export const userDataGuard = () => {
    return async (to, from, next) => {
        if (! store.state.user) {
            await axios.get('/sanctum/csrf-cookie');
            await store.dispatch('loadUser');

            if (! store.state.user && 'login' !== to.name) {
                store.commit('setLoginRedirectUrl', to.fullPath);
            }
        }

        next();
    };
};

/**
 * Navigation guard for Dashboard view.
 *
 * @param {object} to Route being navigated.
 * @param {object} from Route Route being navigated from.
 * @param {Function} next Function called to resolve the quard.
 *
 * @returns {Function} Callback for navigation guard
 */
export const dashboardGuard = (to, from, next) => {
    const userData = store.state.user;

    if (userData) {
        return next();
    }

    if ('/' !== window.location.pathname) {
        store.commit('setLoginRedirectUrl', window.location.pathname);
    }

    next('/login');
};

/**
 * Callback for not authenticated users guard.
 *
 * @param {object} router Vue router instance.
 *
 * @returns {Function} Callback for navigation guard
 */
export const publicGuard = router => (to, from, next) => {
    const userData = store.state.user;

    if (userData && to.meta.disallowAuthed) {
        return router.push('/').catch(() => {});
    }

    return next();
};

/**
 * Callback for pro users guard
 *
 * @param {object} router Vue router instance.
 *
 * @returns {Function} Callback for navigation guard
 */
export const proGuard = router => (to, from, next) => {
    if (to.meta.proUser) {
        const proUser = store.state.user?.pro;

        if (! proUser) {
            return router.push('/').catch(() => {});
        } else {
            next();
        }
    }

    next();
};

/**
 * Callback for offboarding guard
 *
 * @param {object} router Vue router instance.
 *
 * @returns {Function} Callback for navigation guard
 */
export const offboardingGuard = router => (to, from, next) => {
    if (to.meta.offboarding && ! store.state.userSubscription) {
        return router.push('/settings/billing').catch(() => {});
    }

    next();
};

/**
 * Callback for suspended guard
 *
 * @param {object} router Vue router instance.
 *
 * @returns {Function} Callback for navigation guard
 */
export const suspendedGuard = router => (to, from, next) => {
    const suspendedUser = store.state.user?.suspended_at;

    if (suspendedUser) {
        const allSubscriptions = store.state.user?.subscriptions;
        const proSubscriptions = filter(allSubscriptions, (subscription) => {
            return 'pro' === subscription.name;
        });

        if (0 === proSubscriptions.length) {
            next();
        }

        const cancelledSubscriptions = [];

        for (const subscription of proSubscriptions) {
            const formatedCancelationDate = moment(subscription.ends_at);

            if (moment().isSameOrAfter(formatedCancelationDate, 'day')) {
                cancelledSubscriptions.push(subscription);
            }
        }

        if (cancelledSubscriptions.length === proSubscriptions.length) {
            store.commit('changeUIState', { isSuspended: true });
        } else {
            next();
        }
    }

    next();
};

/**
 * Callback for Subscription Change Guard
 * Watches Settings page change and reloads subscription
 *
 * @param {object} router Vue router instance.
 *
 * @returns {Function} Callback for navigation guard
 */
export const subscriptionChangeGuard = router => (to, from, next) => {
    if (
        to.fullPath.includes('settings/billing') ||
        to.fullPath.includes('settings/sites')
    ) {
        const loadedSubscription = store.state.currentSubscription.id;

        if (to.fullPath.includes('billing/lifetime/')) {
            const id = Number(to.params.id);

            if (id !== loadedSubscription) {
                // Load subscription by id.
                store.dispatch('loadUserSubscription', { id });
            }
        } else if (null !== loadedSubscription) {
            const subscription = find(store.state.user.subscriptions, item => 'lifetime' !== item.name);
            const args = subscription ? { id: subscription.id } : {};

            // Load default subscription.
            store.dispatch('loadUserSubscription', args);
        }
    }

    next();
};

/**
 * Settings page free user redirect guard
 *
 * @param {object} router Vue router instance.
 *
 * @returns {Function} Callback for navigation guard
 */
export const settingsProUserGuard = router => (to, from, next) => {
    if (! store.state.user?.pro &&
        (
            to.fullPath.includes('settings/billing') ||
            to.fullPath.includes('settings/sites')
        )) {
        return router.push('/settings').catch(() => {});
    }

    next();
};

/**
 * Opens modal from url hash
 *
 * @returns {Function} Callback for navigation guard
 */
export const modalGuard = () => (to, from, next) => {
    if (store.state.user && to.hash) {
        const uiProperty = `is${upperFirst(camelCase(to.hash.replace('#', '')))}ModalVisible`;

        if (uiProperty in store.state.UIState) {
            store.commit('changeUIState', { [uiProperty]: true });
        }
    }

    next();
};
